import { useSyncExternalStore } from "react";

/**
 * Tracks whether the user has navigated within the app this session — i.e.
 * whether `router.history.back()` will land on a page inside the app rather
 * than exiting it.
 *
 * The state is module-scoped so it persists across route mounts. Without
 * this, a per-component subscription would always start from "no history"
 * on the first render of every newly-visited detail page (because the
 * component only mounts after the navigation event has already fired),
 * and the back button would incorrectly fall back to the parent link
 * instead of returning the user to where they came from on cross-flow
 * navigation (e.g. defect detail → equipment detail).
 *
 * `setupNavigationHistoryTracker` must be called once at app bootstrap so
 * the subscription is active before any route renders.
 */

let pathnames: string[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot() {
  return pathnames.length;
}

interface MinimalRouter {
  state: { location: { pathname: string } };
  subscribe: (event: "onResolved", callback: () => void) => () => void;
}

/**
 * Wire the router up to the navigation tracker. Call once during bootstrap
 * (alongside `createRouter`) so the subscription is in place before the
 * initial route resolves.
 */
export function setupNavigationHistoryTracker(router: MinimalRouter): void {
  router.subscribe("onResolved", () => {
    const path = router.state.location.pathname;
    const last = pathnames[pathnames.length - 1];
    if (path === last) return;
    pathnames.push(path);
    emit();
  });
}

/**
 * `true` when the user has navigated at least once within the app this
 * session. Use this to decide whether `router.history.back()` is safe
 * (returns to a previous in-app page) vs. needing a parent fallback
 * (would exit the app).
 */
export function useHasInAppHistory(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot) > 1;
}
